import { moduleText as ui } from "../../i18n/moduleText";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { askDerick, createSupportTicket } from "./supportApi";
import { getDerickPageContext } from "./derickPageContext";
import { questionForAnswer } from "./derickConversation";
import "./DerickSupportAssistant.css";
import {canOpenDerickWorkspace} from '../derick/derickWorkspaceAccess';

const PUBLIC_PATHS = new Set([
  "/auth",
  "/pricing",
  "/privacy-policy",
  "/terms-of-service",
  "/account-deletion",
]);

const STARTER_MESSAGE = {
  role: "assistant",
  content:
    "Hi, I’m Derick, the SBS AI support assistant. Ask me how to use an SBS feature, or choose Report an issue and I’ll prepare a ticket for System Administration.",
  resolved: true,
  links: [],
};

function messageKey(message, index) {
  return `${message.role}-${index}-${message.content.slice(0, 24)}`;
}

export default function DerickSupportAssistant() {
  const { i18n, t } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const { currentUser, userData } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([STARTER_MESSAGE]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketBusy, setTicketBusy] = useState(false);
  const [ticketResult, setTicketResult] = useState("");
  const [ticketError, setTicketError] = useState("");
  const [ticket, setTicket] = useState({ subject: "", description: "", priority: "normal" });
  const endRef = useRef(null);
  const questionRef = useRef(null);

  const blocked = ["suspended", "revoked"].includes(
    String(userData?.accessStatus || "").toLowerCase()
  );
  const hidden =
    !currentUser ||
    !userData ||
    blocked ||
    PUBLIC_PATHS.has(location.pathname) ||
    location.pathname.startsWith("/join/") ||
    location.pathname.startsWith("/pay/invoice/");

  const conversation = useMemo(
    () => messages.map(({ role, content }) => ({ role, content })).slice(-10),
    [messages]
  );
  const pageContext = useMemo(
    () => getDerickPageContext(location.pathname),
    [location.pathname, language]
  );

  useEffect(() => {
    const openDerick = (event) => {
      setIsOpen(true);
      setTicketOpen(false);
      if (event?.detail?.question) setQuestion(String(event.detail.question));
      window.setTimeout(() => questionRef.current?.focus(), 0);
    };
    window.addEventListener("sbs-open-derick", openDerick);
    return () => window.removeEventListener("sbs-open-derick", openDerick);
  }, []);

  useEffect(() => {
    if (isOpen) endRef.current?.scrollIntoView({ block: "nearest" });
  }, [isOpen, messages, ticketOpen]);

  if (hidden) return null;

  const localizedError = (error, fallback) => {
    const message = typeof error?.message === "string" ? error.message : "";
    const translated = ui(message);
    return message && (!language?.startsWith("es") || translated !== message)
      ? translated
      : ui(fallback);
  };

  const openTicket = (sourceMessage = null, messageIndex = -1) => {
    const sourceQuestion = questionForAnswer(messages, messageIndex);
    setTicket({
      subject: sourceMessage
        ? ui("Help with {{topic}}", { topic: ui(sourceMessage.topic || pageContext.title) })
        : "",
      description: sourceMessage && sourceQuestion
        ? `${ui("I need help with this question:")} ${sourceQuestion}\n\n${ui("Derick's answer did not resolve it.")}`
        : "",
      priority: "normal",
    });
    setTicketError("");
    setTicketResult("");
    setTicketOpen(true);
    setIsOpen(true);
  };

  const openSupportLink = (link) => {
    if (!link?.route) return;
    if (link.route.toLowerCase().endsWith(".pdf")) {
      window.open(link.route, "_blank", "noopener,noreferrer");
    } else {
      setIsOpen(false);
      navigate(link.route);
    }
  };

  const recordLocalFeedback = (messageIndex, rating) => {
    setMessages((current) => current.map((message, index) => (
      index === messageIndex ? { ...message, feedback: rating } : message
    )));
  };

  const askAnotherQuestion = () => {
    setQuestion("");
    window.setTimeout(() => questionRef.current?.focus(), 0);
  };

  const sendQuestion = async (rawQuestion) => {
    const cleanQuestion = String(rawQuestion || "").trim();
    if (!cleanQuestion || busy) return;
    const userMessage = { role: "user", content: cleanQuestion };
    const nextConversation = [...conversation, userMessage].slice(-10);
    setMessages((current) => [...current, userMessage]);
    setQuestion("");
    setBusy(true);
    try {
      const result = await askDerick({
        language,
        question: cleanQuestion,
        pagePath: location.pathname,
        conversation: nextConversation,
      });
      setMessages((current) => [...current, {
        role: "assistant",
        content: result.answer,
        resolved: result.resolved,
        suggestTicket: result.suggestTicket,
        topic: result.topic,
        links: result.links || [],
      }]);
    } catch (error) {
      setMessages((current) => [...current, {
        role: "assistant",
        content: localizedError(error, "I couldn't answer right now. You can still send a support ticket."),
        resolved: false,
        suggestTicket: true,
        links: [],
      }]);
    } finally {
      setBusy(false);
    }
  };

  const submitQuestion = (event) => {
    event?.preventDefault();
    return sendQuestion(question);
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    setTicketError("");
    setTicketResult("");
    if (!ticket.subject.trim() || !ticket.description.trim()) {
      setTicketError(ui("Add a short subject and describe the issue before sending."));
      return;
    }
    setTicketBusy(true);
    try {
      const result = await createSupportTicket({
        language,
        ...ticket,
        category: "sbs_support",
        pagePath: location.pathname,
        conversation,
      });
      setTicketResult(ui("{{ticketNumber}} was sent to SBS System Administration.", {
        ticketNumber: result.ticketNumber,
      }));
    } catch (error) {
      setTicketError(localizedError(error, "The support ticket could not be sent."));
    } finally {
      setTicketBusy(false);
    }
  };

  return (
    <div className="derick-support" data-html2canvas-ignore="true">
      {isOpen && (
        <section className="derick-support__window" role="dialog" aria-label={ui("Derick SBS support assistant")}>
          <header className="derick-support__header">
            <img
              className="derick-support__portrait"
              src="/assets/derick-support-avatar.png"
              alt=""
              aria-hidden="true"
            />
            <div>
              <strong>{location.pathname==='/derick'?t('derickWorkspace.pageHelp'):ui("Ask Derick")}</strong>
              <span>{ui("Helping with ")}{pageContext.title}</span>
              <small>{ui("How may I help you?")}</small>
            </div>
            <button type="button" onClick={() => setIsOpen(false)} aria-label={ui("Close Derick support")}>×</button>
          </header>

          <div className="derick-support__notice">{ui("Product-use help only. Derick cannot fill forms or provide business, financial, accounting, tax, or legal advice. Don’t share passwords, payment details, bank information, or security codes.")}</div>
          {location.pathname!=='/derick'&&canOpenDerickWorkspace(userData)&&<button type="button" className="derick-support__workspace-link" onClick={()=>{setIsOpen(false);navigate('/derick');}}>{t('derickWorkspace.open')}</button>}

          <div className="derick-support__messages" aria-live="polite">
            {messages.map((message, index) => (
              <article key={messageKey(message, index)} className={`derick-support__message is-${message.role}`}>
                <span>{message.role === "assistant" ? ui("Derick") : ui("You")}</span>
                <p>{index === 0 && message === STARTER_MESSAGE ? ui(message.content) : message.content}</p>
                {message.links?.length > 0 && (
                  <div className="derick-support__links">
                    {message.links.slice(0, 1).map((link) => (
                      <button
                        type="button"
                        key={`${link.route}-${link.label}`}
                        onClick={() => openSupportLink(link)}
                      >
                        {link.route.toLowerCase().endsWith(".pdf")
                          ? ui("Open guide: {{label}}", { label: ui(link.label) })
                          : ui("Show me where: {{label}}", { label: ui(link.label) })}
                      </button>
                    ))}
                  </div>
                )}
                {message.role === "assistant" && index > 0 && (
                  <div className="derick-support__answer-actions" aria-label={ui("Derick answer actions")}>
                    <button
                      type="button"
                      className={message.feedback === "helpful" ? "is-selected" : ""}
                      onClick={() => recordLocalFeedback(index, "helpful")}
                    >{ui("Helpful")}</button>
                    <button
                      type="button"
                      className={message.feedback === "not_helpful" ? "is-selected" : ""}
                      onClick={() => recordLocalFeedback(index, "not_helpful")}
                    >{ui("Not helpful")}</button>
                    <button type="button" onClick={askAnotherQuestion}>{ui("Ask another question")}</button>
                  </div>
                )}
                {message.feedback === "helpful" && (
                  <p className="derick-support__feedback-note" role="status">{ui("Thanks—glad that answered your question.")}</p>
                )}
                {message.role === "assistant" && message.suggestTicket && (
                  <button type="button" className="derick-support__ticket-link" onClick={() => openTicket(message, index)}>{ui("Prepare a support ticket")}</button>
                )}
                {message.role === "assistant" && message.feedback === "not_helpful" && !message.suggestTicket && (
                  <button type="button" className="derick-support__ticket-link" onClick={() => openTicket(message, index)}>{ui("Prepare a support ticket")}</button>
                )}
              </article>
            ))}
            {busy && <div className="derick-support__typing">{ui("Derick is checking SBS help…")}</div>}
            <div ref={endRef} />
          </div>

          {ticketOpen ? (
            <form className="derick-support__ticket" onSubmit={submitTicket}>
              <div className="derick-support__ticket-heading">
                <strong>{ui("Send to System Administration")}</strong>
                <button type="button" onClick={() => setTicketOpen(false)}>{ui("Back to chat")}</button>
              </div>
              <label>{ui("Subject")}<input
                  value={ticket.subject}
                  maxLength="180"
                  onChange={(event) => setTicket((current) => ({ ...current, subject: event.target.value }))}
                />
              </label>
              <label>{ui("What happened?")}<textarea
                  value={ticket.description}
                  rows="5"
                  maxLength="4000"
                  placeholder={ui("Describe what happened, what you expected, and any message shown on the screen.")}
                  onChange={(event) => setTicket((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
              <label>{ui("Priority")}<select
                  value={ticket.priority}
                  onChange={(event) => setTicket((current) => ({ ...current, priority: event.target.value }))}
                >
                  <option value="normal">{ui("Normal")}</option>
                  <option value="high">{ui("High — blocking my work")}</option>
                </select>
              </label>
              {ticketError && <p className="derick-support__error" role="alert">{ticketError}</p>}
              {ticketResult && <p className="derick-support__success" role="status">{ticketResult}</p>}
              {!ticketResult && (
                <button type="submit" className="derick-support__send" disabled={ticketBusy}>
                  {ticketBusy ? ui("Sending…") : ui("Confirm and send ticket")}
                </button>
              )}
            </form>
          ) : (
            <>
              {messages.length === 1 && (
                <div className="derick-support__starters">
                  {pageContext.prompts.map((prompt) => (
                    <button
                      type="button"
                      key={prompt}
                      onClick={() => sendQuestion(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}
              <form className="derick-support__composer" onSubmit={submitQuestion}>
                <label className="sr-only" htmlFor="derick-question">{ui("Ask Derick an SBS question")}</label>
                <textarea
                  id="derick-question"
                  ref={questionRef}
                  value={question}
                  rows="2"
                  maxLength="1600"
                  placeholder={ui("Ask an SBS question…")}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      submitQuestion();
                    }
                  }}
                />
                <button type="submit" disabled={busy || !question.trim()}>{ui("Send")}</button>
              </form>
              <button type="button" className="derick-support__report" onClick={() => openTicket()}>{ui("Report an issue")}</button>
            </>
          )}
        </section>
      )}

      <button
        type="button"
        className="derick-support__launcher"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-label={isOpen ? ui("Close Derick support") : ui("Ask Derick for SBS help")}
      >
        <img src="/assets/derick-support-avatar.png" alt="" aria-hidden="true" />
        <strong>{isOpen ? ui("Close") : location.pathname==='/derick'?t('derickWorkspace.pageHelp'):ui("Ask Derick")}</strong>
      </button>
    </div>
  );
}
